import { AnimatePresence, motion } from 'framer-motion';
import {
    Loader,
    UserRound,
    Copy,
    FileText,
    Presentation,
    Table,
} from 'lucide-react';
import React, { useRef } from 'react';
import { cn } from '~/lib/utils';
import { t } from '~/services/i18n';
import { useMessagesAPI } from '~/hooks';
import copy from 'copy-to-clipboard';
import MarkdownViewer from '../MarkdownViewer';

import { Avatar, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';

interface ChatListProps {
    messages: any[];
    isFetching: boolean;
    isWord?: boolean;
    isPowerPoint?: boolean;
    isExcel?: boolean;
}

export default function ChatList({
    isFetching,
    messages,
    isWord,
    isPowerPoint,
    isExcel,
}: ChatListProps) {
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const lastMessageRef = useRef<HTMLDivElement>(null);
    const messageApi = useMessagesAPI();

    // Show welcome message if no messages exist
    const displayMessages =
        messages.length === 0
            ? [
                  {
                      isMine: false,
                      message: t(
                          'chat.welcomeMessage.new',
                          'Hello 👋 How can I help you? I can analyze and answer questions about your currently opened Word, Excel, or PowerPoint document. Type your question below 👇',
                      ),
                  },
              ]
            : messages;

    const dataItems = isFetching
        ? [...displayMessages, { isMine: false, isTyping: true }]
        : displayMessages;

    React.useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (lastMessageRef.current) {
                lastMessageRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }
        }, 150);

        return () => clearTimeout(timeoutId);
    }, [messages]);

    function getItemText(item) {
        if (item.isEmpty) {
            return t(
                'chat.emptyResponse',
                'Sorry, I could not find an answer to your query.',
            );
        }

        return (
            item.message ||
            item.error ||
            (item.isTyping &&
                t('chat.assistantTyping', 'Assistant is typing...'))
        );
    }


    const copyToClipboard = (text: string) => {
        const success = copy(text, { format: 'text/plain' });
        if (success) {
            messageApi?.info(
                t('chat.copiedToClipboard', 'Copied to clipboard'),
            );
        } else {
            messageApi?.error(
                t('chat.copyFailed', 'Failed to copy to clipboard'),
            );
        }
    };

    const insertToDocument = async (text: string) => {
        try {
            if (isWord) {
                const Word = (window as any).Word;
                if (!Word) {
                    messageApi?.error(
                        t('chat.wordNotAvailable', 'Word is not available'),
                    );
                    return;
                }

                await Word.run(async (context: any) => {
                    const selection = context.document.getSelection();
                    selection.insertText(text, Word.InsertLocation.replace);
                    await context.sync();
                });

                messageApi?.info(
                    t('chat.insertedToDocument', 'Inserted to document'),
                );
            } else if (isPowerPoint) {
                const PowerPoint = (window as any).PowerPoint;
                if (!PowerPoint) {
                    messageApi?.error(
                        t(
                            'chat.powerPointNotAvailable',
                            'PowerPoint is not available',
                        ),
                    );
                    return;
                }

                await PowerPoint.run(async (context: any) => {
                    try {
                        const slides = context.presentation.slides;
                        context.load(slides, 'items');
                        await context.sync();

                        let targetSlide;
                        if (slides.items.length > 0) {
                            // Use the first slide as default
                            targetSlide = slides.items[0];
                        }

                        if (targetSlide) {
                            // Add a text box with the content
                            targetSlide.shapes.addTextBox(text, {
                                left: 50,
                                top: 100,
                                width: 600,
                                height: 200,
                            });
                            await context.sync();
                        }
                    } catch (insertError) {
                        console.error(
                            'PowerPoint insertion error:',
                            insertError,
                        );
                        // Fallback: try simpler insertion
                        const slides = context.presentation.slides;
                        context.load(slides, 'items');
                        await context.sync();

                        if (slides.items.length > 0) {
                            slides.items[0].shapes.addTextBox(text);
                            await context.sync();
                        }
                    }
                });

                messageApi?.info(
                    t('chat.insertedToSlide', 'Inserted to slide'),
                );
            } else if (isExcel) {
                const Excel = (window as any).Excel;
                if (!Excel) {
                    messageApi?.error(
                        t('chat.excelNotAvailable', 'Excel is not available'),
                    );
                    return;
                }

                await Excel.run(async (context: any) => {
                    try {
                        const selectedRange =
                            context.workbook.getSelectedRange();
                        context.load(selectedRange, ['address']);
                        await context.sync();

                        // Set the value to the selected cell(s)
                        selectedRange.values = [[text]];
                        await context.sync();
                    } catch (insertError) {
                        console.error('Excel insertion error:', insertError);
                        // Fallback: try to insert at A1
                        const sheet =
                            context.workbook.worksheets.getActiveWorksheet();
                        const range = sheet.getRange('A1');
                        range.values = [[text]];
                        await context.sync();
                    }
                });

                messageApi?.info(t('chat.insertedToCell', 'Inserted to cell'));
            }
        } catch (error) {
            console.error('Failed to insert to document:', error);
            messageApi?.error(
                t('chat.insertFailed', 'Failed to insert to document'),
            );
        }
    };

    return (
        <div
            ref={messagesContainerRef}
            className='w-full overflow-y-auto overflow-x-hidden h-full flex flex-col'>
            <AnimatePresence>
                {dataItems.map((item, index) => (
                    <motion.div
                        key={index}
                        ref={
                            index === dataItems.length - 1
                                ? lastMessageRef
                                : null
                        }
                        layout
                        initial={{ opacity: 0, scale: 1, y: 50, x: 0 }}
                        animate={{ opacity: 1, scale: 1, y: 0, x: 0 }}
                        exit={{ opacity: 0, scale: 1, y: 1, x: 0 }}
                        transition={{
                            opacity: { duration: 0.1 },
                            layout: {
                                type: 'spring',
                                bounce: 0.3,
                                duration: dataItems.indexOf(item) * 0.05 + 0.2,
                            },
                        }}
                        style={{
                            originX: 0.5,
                            originY: 0.5,
                        }}
                        className={cn(
                            'flex flex-col gap-2 p-2 whitespace-pre-wrap',
                            item.isMine ? 'items-end' : 'items-start',
                        )}>
                        <div className='flex gap-3 items-center'>
                            {!item.isMine && (
                                <Avatar className='flex justify-center items-center border-2 border-slate-200 bg-white'>
                                    <AvatarImage
                                        src={'/assets/64.png'}
                                        alt={`AI Perfect Assistant Logo`}
                                        className='w-[24px] h-[24px]'
                                    />
                                </Avatar>
                            )}
                            <div className='grid gap-y-1'>
                                <div
                                    className={
                                        'bg-slate-200 p-3 rounded-md max-w-md'
                                    }>
                                    {item.isTyping ? (
                                        <div className='flex items-center'>
                                            <Loader className='w-3 h-3 mr-2 animate-spin' />
                                            <span>{getItemText(item)}</span>
                                        </div>
                                    ) : !item.isMine ? (
                                        <MarkdownViewer>
                                            {getItemText(item)}
                                        </MarkdownViewer>
                                    ) : (
                                        <span>{getItemText(item)}</span>
                                    )}
                                </div>

                                {/* Action buttons for AI messages */}
                                {!item.isMine &&
                                    !item.isTyping &&
                                    item.message && (
                                        <div className='flex gap-2 mt-1'>
                                            <Button
                                                variant='outline'
                                                size='sm'
                                                onClick={() =>
                                                    copyToClipboard(
                                                        getItemText(item),
                                                    )
                                                }
                                                className='h-7 px-2 text-xs'>
                                                <Copy className='w-3 h-3 mr-1' />
                                                {t('chat.copy', 'Copy')}
                                            </Button>
                                            {(isWord ||
                                                isPowerPoint ||
                                                isExcel) && (
                                                <Button
                                                    variant='outline'
                                                    size='sm'
                                                    onClick={() =>
                                                        insertToDocument(
                                                            getItemText(item),
                                                        )
                                                    }
                                                    className='h-7 px-2 text-xs'>
                                                    {isWord ? (
                                                        <FileText className='w-3 h-3 mr-1' />
                                                    ) : isPowerPoint ? (
                                                        <Presentation className='w-3 h-3 mr-1' />
                                                    ) : (
                                                        <Table className='w-3 h-3 mr-1' />
                                                    )}
                                                    {isWord
                                                        ? t(
                                                              'chat.insertToDocument',
                                                              'Insert to Document',
                                                          )
                                                        : isPowerPoint
                                                          ? t(
                                                                'chat.insertToSlide',
                                                                'Insert to Slide',
                                                            )
                                                          : t(
                                                                'chat.insertToCell',
                                                                'Insert to Cell',
                                                            )}
                                                </Button>
                                            )}
                                        </div>
                                    )}
                            </div>
                            {item.isMine && (
                                <Avatar className='flex justify-center items-center bg-slate-300 transition-all group-hover:scale-110'>
                                    <UserRound />
                                </Avatar>
                            )}
                        </div>
                    </motion.div>
                ))}
            </AnimatePresence>
        </div>
    );
}
